import { useSetAtom } from "jotai";

import { useEffect, useRef } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { classifyQueryError } from "./classifyQueryError";
import { globalErrorEventAtom } from "./globalErrorEvent";
import { useSetMaintenanceMode } from "./useSetMaintenanceMode";

/**
 * Observes every query/mutation on the app's single shared QueryClient (the
 * one `CpqQueryClientProvider` creates internally — see `src/index.tsx`) and
 * layers our own generic error handling on top, without touching CpqCore's
 * own `queryCache`/`mutationCache` `onError` (which keeps firing
 * independently, so its "DISPLAY_ERROR:" toast convention is unaffected).
 *
 * Renders nothing — mount once, near the root, inside `Providers.tsx`.
 */
export function QueryErrorBridge(): null {
  const queryClient = useQueryClient();
  const setMaintenanceMode = useSetMaintenanceMode();
  const setErrorEvent = useSetAtom(globalErrorEventAtom);
  const nextErrorId = useRef(0);

  useEffect(() => {
    const handleError = (error: unknown): void => {
      const classified = classifyQueryError(error);
      if (classified.kind === "maintenance") {
        setMaintenanceMode(true);
        return;
      }
      if (classified.kind === "toast") {
        nextErrorId.current += 1;
        setErrorEvent({
          id: nextErrorId.current,
          key: classified.key,
          params: classified.params,
          fallbackMessage: classified.fallbackMessage,
        });
      }
    };
    const handleSuccess = (): void => setMaintenanceMode(false);

    const unsubscribeQueries = queryClient
      .getQueryCache()
      .subscribe((event) => {
        if (event.type !== "updated") return;
        if (event.action.type === "error") handleError(event.query.state.error);
        else if (event.action.type === "success") handleSuccess();
      });
    const unsubscribeMutations = queryClient
      .getMutationCache()
      .subscribe((event) => {
        if (event.type !== "updated") return;
        if (event.action.type === "error")
          handleError(event.mutation.state.error);
        else if (event.action.type === "success") handleSuccess();
      });

    return () => {
      unsubscribeQueries();
      unsubscribeMutations();
    };
  }, [queryClient, setMaintenanceMode, setErrorEvent]);

  return null;
}
